// The Events tab's "Week Board" model (#450) — pure, deterministic, no React.
//
// One question decides the whole screen: on a Friday morning, how full is my week and what is on
// tonight? So the board answers it twice — a 7-day strip of counts across the top, and, below it,
// the same upcoming events grouped into RELATIVE buckets (Today / Tomorrow / This weekend / Next
// week / Later) and then by day. Grouping by date alone is a wall of numbers; grouping by village
// hides "tonight".
//
// Everything here keys off `beirutDayKey` (src/domain/eventTime.js), the single source of truth
// the Guide "Coming up" card, the catalog filters and the ask-Zgharta ranking already share. A
// second day rule would put the Events tab and the Guide card a day apart between Beirut midnight
// and 02:00-03:00, which is exactly the class of bug that helper exists to close.

import { beirutDayKey, beirutDayStartUtcMs } from '../domain/eventTime';
import { matchVillageOwner } from '../domain/cazaRegisters';

const DAY_MS = 86400000;

// The board covers a week starting today, so a visitor sees "the next seven days" and not a
// calendar month they have to read around.
export const WEEK_BOARD_DAYS = 7;

// The night bar degrades to a text count past this many segments: a month-long exhibition drawn
// as 30 hairlines is noise, not information (#450 design note, "Multi-day handling").
export const MAX_RUN_BAR_SEGMENTS = 7;

// A stored range longer than this is data damage, not a festival. Enumerating it would build a
// multi-thousand-entry array on every render, so the run is refused and the event falls back to
// its single start day.
const MAX_RUN_DAYS = 366;

// Bucket order is render order. `past` is not in this list on purpose — past events leave the
// main flow entirely and live in the bottom disclosure.
export const EVENT_BUCKET_KEYS = ['today', 'tomorrow', 'weekend', 'next', 'later'];

function dayIndexOf(dayKey) {
  const ms = beirutDayStartUtcMs(dayKey);
  return ms == null ? null : new Date(ms).getUTCDay();
}

// Whole days from `fromKey` to `toKey`. Both are canonical UTC-midnight anchors for a Beirut
// calendar day, so this is exact integer arithmetic and never drifts across a DST change.
function dayDelta(fromKey, toKey) {
  const from = beirutDayStartUtcMs(fromKey);
  const to = beirutDayStartUtcMs(toKey);
  if (from == null || to == null) return null;
  return Math.round((to - from) / DAY_MS);
}

export function addDays(dayKey, count) {
  const ms = beirutDayStartUtcMs(dayKey);
  if (ms == null) return null;
  return new Date(ms + count * DAY_MS).toISOString().slice(0, 10);
}

// 5 = Friday, 6 = Saturday, 0 = Sunday. Lebanon's weekend is Saturday + Sunday and Friday night
// is when the weekend starts being planned, so the board tints all three.
export function isWeekendDayKey(dayKey) {
  const index = dayIndexOf(dayKey);
  return index === 5 || index === 6 || index === 0;
}

/**
 * Which relative bucket a day belongs to, relative to `nowKey`.
 *
 * The one rule worth stating out loud: `d === 1` is "Tomorrow" ONLY when tomorrow is a weekday.
 * On a Friday, tomorrow is Saturday, and a visitor reads that as part of the weekend, not as a
 * separate day — so Saturday and Sunday fold into `weekend` instead. `d <= 2` bounds the weekend
 * window so a Friday's board never reaches past Sunday.
 */
export function eventBucketKey(dayKey, nowKey) {
  const delta = dayDelta(nowKey, dayKey);
  if (delta == null) return null;
  const index = dayIndexOf(dayKey);
  if (delta < 0) return 'past';
  if (delta === 0) return 'today';
  if (delta === 1 && !(index === 6 || index === 0)) return 'tomorrow';
  if (delta <= 2 && (index === 5 || index === 6 || index === 0)) return 'weekend';
  if (delta <= 9) return 'next';
  return 'later';
}

/**
 * Every night a multi-day event runs, as Beirut day keys.
 *
 * `total` is the true length even when the bar is capped, because "12 days" is a fact the visitor
 * needs and 7 drawn segments would be a lie. An unreadable or backwards `endDate` is ignored
 * rather than trusted — a single-day fallback is a gap, a wrong range is a wrong answer.
 */
export function eventNights(event, { max = MAX_RUN_BAR_SEGMENTS } = {}) {
  const startKey = beirutDayKey(event?.date);
  if (!startKey) return { dayKeys: [], total: 0, capped: false, isRun: false };
  const rawEndKey = event?.endDate ? beirutDayKey(event.endDate) : null;
  const endKey = rawEndKey && rawEndKey > startKey ? rawEndKey : null;
  if (!endKey) return { dayKeys: [startKey], total: 1, capped: false, isRun: false };

  const span = dayDelta(startKey, endKey);
  if (span == null || span > MAX_RUN_DAYS) {
    return { dayKeys: [startKey], total: 1, capped: false, isRun: false };
  }
  const total = span + 1;
  const dayKeys = [];
  for (let i = 0; i < total; i += 1) dayKeys.push(addDays(startKey, i));
  return {
    dayKeys: total > max ? dayKeys.slice(0, max) : dayKeys,
    total,
    capped: total > max,
    isRun: true,
  };
}

/** Every night of a run, uncapped — what the board's per-day counts and its day filter read. */
export function eventRunDayKeys(event) {
  const { total } = eventNights(event, { max: MAX_RUN_DAYS });
  const startKey = beirutDayKey(event?.date);
  if (!startKey) return [];
  const keys = [];
  for (let i = 0; i < total; i += 1) keys.push(addDays(startKey, i));
  return keys;
}

/**
 * The night a run is "at" for a visitor reading today's board: the first night that has not
 * already passed. A festival that opened yesterday and runs to Sunday belongs under today, not
 * under the day it opened — the existing `endDate || date` upcoming rule already keeps it in the
 * list, so filing it under a past day would hide it below every other card.
 */
export function firstRemainingNight(event, nowKey) {
  const keys = eventRunDayKeys(event);
  if (!keys.length) return null;
  return keys.find(key => key >= nowKey) || keys[keys.length - 1];
}

// Production stores both `20:30` and `7:00 PM` (see src/admin/adminWrites.js `checkEventTime`),
// so a plain string sort would put "7:00 PM" before "20:30" and both before "08:00". Minutes are
// the only comparable form. An unreadable time sorts LAST, with the events that have no time at
// all — "we do not know when" is not "first thing in the morning".
export function eventStartMinutes(time) {
  if (typeof time !== 'string') return null;
  const match = time.trim().match(/^(\d{1,2})[:.](\d{2})\s*(am|pm)?/i);
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const suffix = match[3] ? match[3].toLowerCase() : null;
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || minutes > 59) return null;
  if (suffix) {
    if (hours < 1 || hours > 12) return null;
    if (suffix === 'pm' && hours !== 12) hours += 12;
    if (suffix === 'am' && hours === 12) hours = 0;
  } else if (hours > 23) {
    return null;
  }
  return hours * 60 + minutes;
}

function compareWithinDay(a, b) {
  const aMinutes = eventStartMinutes(a.time);
  const bMinutes = eventStartMinutes(b.time);
  if (aMinutes == null && bMinutes == null) return 0;
  if (aMinutes == null) return 1;
  if (bMinutes == null) return -1;
  return aMinutes - bMinutes;
}

function groupByDay(entries) {
  // A plain object, never a `Map` — `src/App.js` aliases lucide's Map icon over the global
  // constructor, and this module is imported from there (CLAUDE.md's `Map`-shadowing gotcha).
  const byDay = {};
  const order = [];
  entries.forEach(entry => {
    if (!byDay[entry.dayKey]) {
      byDay[entry.dayKey] = [];
      order.push(entry.dayKey);
    }
    byDay[entry.dayKey].push(entry.event);
  });
  order.sort();
  return order.map(dayKey => ({
    dayKey,
    // `slice()` first: `sort` mutates, and the array it would mutate is the one we just built
    // from the caller's list.
    events: byDay[dayKey].slice().sort(compareWithinDay),
  }));
}

/**
 * The whole Events screen model, from a raw event list and "now".
 *
 * Returns upcoming events grouped bucket -> day -> time, the past events grouped by day
 * (newest first — a visitor scrolling into the disclosure wants last week before last year),
 * and the id of the next upcoming event so exactly one card can carry the `Next up` mark.
 */
export function bucketUpcomingEvents(events = [], now = new Date()) {
  const nowKey = beirutDayKey(now) ?? '';
  const upcomingEntries = [];
  const pastEntries = [];
  const undated = [];

  events.forEach(event => {
    const runDayKeys = eventRunDayKeys(event);
    const startKey = runDayKeys[0];
    const lastKey = runDayKeys[runDayKeys.length - 1];
    if (!startKey || !lastKey) {
      // A row whose date cannot be read is still a real event. It keeps its place in the list
      // rather than vanishing, at the end, where the card renders its own unknown-date fallback.
      undated.push(event);
      return;
    }
    if (lastKey < nowKey) {
      pastEntries.push({ dayKey: startKey, event });
      return;
    }
    upcomingEntries.push({ dayKey: firstRemainingNight(event, nowKey) || startKey, event });
  });

  const buckets = EVENT_BUCKET_KEYS.map(key => ({
    key,
    days: groupByDay(upcomingEntries.filter(entry => eventBucketKey(entry.dayKey, nowKey) === key)),
  })).filter(bucket => bucket.days.length > 0);

  const orderedUpcoming = buckets.flatMap(bucket => bucket.days.flatMap(day => day.events));
  const past = groupByDay(pastEntries).reverse();

  return {
    nowKey,
    buckets,
    past,
    undated,
    upcoming: orderedUpcoming,
    upcomingCount: orderedUpcoming.length + undated.length,
    nextEventId: orderedUpcoming.length ? orderedUpcoming[0].id : null,
  };
}

/**
 * The 7 day buttons across the top: one per day from today, each with the number of events on
 * THAT day. A multi-night run counts on every one of its nights, because "is anything on
 * Saturday" is answered by the festival that runs Thursday to Sunday.
 */
export function weekBoardDays(events = [], now = new Date(), { days = WEEK_BOARD_DAYS } = {}) {
  const nowKey = beirutDayKey(now) ?? '';
  const counts = {};
  events.forEach(event => {
    const runDayKeys = eventRunDayKeys(event);
    const lastKey = runDayKeys[runDayKeys.length - 1];
    if (!lastKey || lastKey < nowKey) return;
    runDayKeys.forEach(key => {
      counts[key] = (counts[key] || 0) + 1;
    });
  });
  const board = [];
  for (let i = 0; i < days; i += 1) {
    const dayKey = addDays(nowKey, i);
    if (!dayKey) break;
    board.push({
      dayKey,
      count: counts[dayKey] || 0,
      isToday: i === 0,
      isWeekend: isWeekendDayKey(dayKey),
    });
  }
  return board;
}

/** Weekend vs weekday split of the board, for the one-line caption under it. */
export function weekBoardSplit(board = []) {
  return board.reduce((split, day) => ({
    weekend: split.weekend + (day.isWeekend ? day.count : 0),
    rest: split.rest + (day.isWeekend ? 0 : day.count),
  }), { weekend: 0, rest: 0 });
}

// Events carry no coordinates — the ingest stores a venue string and a village, nothing more —
// so Directions is a Google Maps SEARCH, not a route from a point. `location` is the venue a
// visitor would type; when it is missing, the village and its caza are the most specific honest
// query left. `Lebanon` is appended so a village name that also exists elsewhere resolves here.
export function eventDirectionsQuery(event, { cazaLabel } = {}) {
  const parts = [];
  const location = typeof event?.location === 'string' ? event.location.trim() : '';
  const village = typeof event?.village === 'string' ? event.village.trim() : '';
  const caza = typeof cazaLabel === 'string' ? cazaLabel.trim() : '';
  if (location) parts.push(location);
  if (village && village !== location) parts.push(village);
  if (village && matchVillageOwner(village)?.ambiguous && caza && !parts.includes(caza)) parts.push(caza);
  if (!parts.length && caza) parts.push(caza);
  parts.push('Lebanon');
  return parts.join(', ');
}

export function eventDirectionsUrl(event, options) {
  const query = eventDirectionsQuery(event, options);
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

// The credit line is TEXT AND A LINK, never the source's own copy: the ingest deliberately stores
// no third-party description (CLAUDE.md, "The ingest never stores a source description"), and the
// visitor is owed a way to check the row against its listing.
const EVENT_SOURCE_DOMAINS = {
  ticketingboxoffice: 'ticketingboxoffice.com',
  lebtivity: 'lebtivity.com',
  lebanontraveler: 'lebanontraveler.com',
};

export function eventSourceCredit(event) {
  const source = typeof event?.source === 'string' ? event.source.trim() : '';
  if (!source) return { kind: 'first-party', domain: null, url: null };
  const known = EVENT_SOURCE_DOMAINS[source] || null;
  const raw = typeof event?.sourceUrl === 'string' ? event.sourceUrl.trim() : '';

  // The URL is PARSED, never pattern-matched. A raw-string test accepts
  // `https://lebtivity.com@evil.example/x`, whose real host is `evil.example` — and the credit
  // line would have labelled it "Listed by lebtivity.com" and linked it, which is a phishing
  // shape this app hands to a visitor who trusts the guide. Only `https:` is offered, so a stored
  // `javascript:` or `http:` value is dropped rather than rendered.
  let parsed = null;
  try {
    const candidate = new URL(raw);
    if (candidate.protocol === 'https:' && candidate.hostname) parsed = candidate;
  } catch {
    parsed = null;
  }
  // Trailing dot stripped: `lebtivity.com.` resolves to the same host but fails a plain compare.
  const host = parsed ? parsed.hostname.replace(/\.$/, '').toLowerCase() : null;
  // A known source may only link its own domain (or a subdomain of it). An UNKNOWN source has no
  // domain to check against, so the label becomes the link's real host — the line a visitor reads
  // and the place the link goes must be the same thing, always.
  const hostMatchesSource = Boolean(host) && Boolean(known) && (host === known || host.endsWith(`.${known}`));
  if (known) {
    return { kind: 'listed', domain: known, url: hostMatchesSource ? parsed.toString() : null };
  }
  return { kind: 'listed', domain: host || source, url: host ? parsed.toString() : null };
}
